import { v4 as uuidv4 } from 'uuid';
import { getBridge } from './bridge';
import { markDirty } from './sync/outbox';

// Thin abstraction over local config storage. Delegates everything to the active
// bridge (shell bridge in the desktop app, mockBridge/localStorage in the browser) —
// see utils/bridge.js for the actual storage logic per environment.
//
// This is also the ONLY place that marks a config-profile dirty for the sync
// engine (utils/sync/) — every save/delete that goes through here gets synced
// to Postgres when there's a session + connectivity. markDirty is
// best-effort/never-throws, so a sync bookkeeping hiccup never fails the save
// itself (see utils/sync/outbox.js).
class StorageService {
    async getConfigs() {
        try {
            const bridge = getBridge();
            if (!bridge) return [];
            return await bridge.listLocalConfigs();
        } catch (error) {
            console.error('[StorageService] Error getting configs:', error);
            return [];
        }
    }

    async saveConfig(config) {
        try {
            const bridge = getBridge();
            if (!bridge) return { success: false, message: 'Bridge not available' };

            // The client owns the LWW clock: stamp id + updated_at HERE so the value
            // is identical in the browser and the desktop shell. The shell's Rust
            // save returns id/updated_at but can't mutate this JS object, so relying
            // on post-call mutation (like the mock does) would leave updated_at
            // undefined on desktop and break sync ordering. applyIncoming() writes
            // through the bridge directly (not here), preserving the server's LWW ts.
            if (!config.id) config.id = uuidv4();
            config.updated_at = new Date().toISOString();

            const res = await bridge.saveLocalConfig(config);
            if (res.success) {
                await markDirty('config-profile', config.id, { updatedAt: config.updated_at });
                return { success: true, config };
            }
            return { success: false, message: res.message };
        } catch (error) {
            console.error('[StorageService] Error saving config:', error);
            return { success: false, message: error.message };
        }
    }

    async deleteConfig(id) {
        try {
            const bridge = getBridge();
            if (!bridge) return { success: false, message: 'Bridge not available' };
            const res = await bridge.deleteLocalConfig({ id });
            if (res.success) await markDirty('config-profile', id, { deleted: true });
            return res;
        } catch (error) {
            console.error('[StorageService] Error deleting config:', error);
            return { success: false, message: error.message };
        }
    }

    async ensureDefaultConfig(defaultConfigData) {
        const configs = await this.getConfigs();
        if (configs.length === 0) {
            return await this.saveConfig({ name: 'Default Config', settings: defaultConfigData });
        }
        return { success: true, config: configs[0] };
    }
}

export const storageService = new StorageService();
